<template>
  <div class="home-page">
    <div class="background-header"></div>
    <div class="shell">
      <app-topbar @lang-change="lang = $event" />

      <section class="wallet-card">
        <img class="wallet-card-bg" src="@/assets/home/wallet-card-border.svg" alt="" />
        <div class="wallet-card-content">
          <div class="wallet-label">{{ t.walletBalance }}</div>
          <div class="wallet-balance-row">
            <img
              class="eye-icon"
              :src="balanceVisible ? require('@/assets/home/icon-eye.svg') : require('@/assets/home/icon-eye.svg')"
              :style="{ opacity: balanceVisible ? 1 : 0.4 }"
              @click="balanceVisible = !balanceVisible"
              alt=""
            />
            <span class="wallet-amount" v-if="balanceVisible">{{ walletBalance }} USDT</span>
            <span class="wallet-amount" v-else>**** USDT</span>
          </div>
          <button class="connect-btn" type="button" @click="connectWallet">
            <img class="connect-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
            <span class="connect-btn-text">{{ walletAddress ? t.switchWallet : t.connectWallet }}</span>
          </button>
        </div>
      </section>

      <section class="pool-health-card">
        <div style="display: flex; flex-direction: column; align-items: center;">
          <van-circle
            class="pool-health-bg"
            v-model="currentRate"
            :color="gradientColor"
            :stroke-width="80"
            :rate="srtRatio"
            :speed="100"
          />
          <!-- <img class="pool-health-bg" src="@/assets/home/pool-health-gauge.svg" alt="" /> -->
          <div class="pool-health-score">{{ srtRatio }}</div>
          <div class="pool-health-label">POOL HEALTH</div>
          <div class="pool-health-badge">
            <span class="badge-text">{{ t.healthy }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-card">
            <div class="info-title">{{ t.ocPrice }}</div>
            <div class="info-value">$ {{ ocPrice }}</div>
            <div class="info-desc">{{ t.onChainSource }}</div>
          </div>
          <div class="info-card">
            <div class="info-title">{{ t.sellTax }}</div>
            <div class="info-value">{{ sellTax }}%</div>
            <div class="info-desc">{{ t.srtDynamic }}</div>
          </div>
        </div>
      </section>

      <div class="full-card">
        <div class="full-card-top">
          <div class="full-card-title">{{ t.todayYield }}</div>
          <div class="full-card-value">{{ todayYield }}</div>
        </div>
        <div class="full-card-bottom">
          <span class="full-card-desc">{{ t.yieldDesc }}</span>
          <span class="full-card-date">{{ currentDate }}</span>
        </div>
      </div>

      <div class="full-card">
        <div class="full-card-top">
          <div class="full-card-title">{{ t.bonusPool }}</div>
          <div class="full-card-value">{{ bonusPoolAmount }} OC</div>
        </div>
        <div class="full-card-bottom">
          <span class="full-card-desc">{{ t.bonusDesc }}</span>
          <span class="full-card-date">{{ currentDate }}</span>
        </div>
      </div>
    </div>

    <bottom-nav active-tab="home" />

    <div class="toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import AppTopbar from '@/components/AppTopbar.vue'
import BottomNav from '@/components/BottomNav.vue'
import { wallet } from '@/store/wallet'
import { getMessages } from '@/i18n'
import { ethers } from 'ethers'
import UsdtAbi from '@/abi/usdt_abi.json'
import RouterAbi from '@/abi/Router.json'
import OpenClawAbi from '@/abi/OpenClaw.json'

const USDT_ADDRESS = process.env.VUE_APP_USDT_ADDRESS
const OC_ADDRESS = process.env.VUE_APP_OC_ADDRESS
const ALK_ADDRESS = process.env.VUE_APP_ALK_ADDRESS
const ROUTER_ADDRESS = process.env.VUE_APP_ROUTER_ADDRESS
const RPC_URL = process.env.VUE_APP_NEXT_PUBLIC_NODE_1

export default {
  name: 'HomePage',
  components: { AppTopbar, BottomNav },
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      walletBalance: '--',
      ocPrice: '--',
      sellTax: '--',
      bonusPoolAmount: '--',
      todayYield: '--',
      balanceVisible: true,
      toastVisible: false,
      toastMsg: '',
      currentRate: 0,
      srtRatio: 0,
      gradientColor: {
        '0%': '#E41B33',
        '100%': '#E71E55'
      },
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
    currentDate() {
      return new Date().toISOString().slice(0, 10)
    },
    walletAddress() {
      return wallet.address
    }
  },
  watch: {
    walletAddress(addr) {
      if (addr) this.fetchUsdtBalance()
      else this.walletBalance = '--'
    }
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    if (this.walletAddress) this.fetchUsdtBalance()
    this.fetchOcPrice()
    this.fetchSellTax()
    this.fetchBonusPool()
    this.fetchTodayYield()
    this.fetchSrtData()
  },
  methods: {
    async fetchSrtData() {
      if (!OC_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const oc = new ethers.Contract(OC_ADDRESS, OpenClawAbi, provider)
        const [strRate, base] = await Promise.all([oc.strRate(), oc.TAX_BASE()])
        this.srtRatio = Math.round((1 - Number(strRate) / Number(base)) * 100)
      } catch (e) {
        console.error('fetchSrtData failed', e)
      }
    },
    async fetchUsdtBalance() {
      if (!this.walletAddress || !USDT_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const usdt = new ethers.Contract(USDT_ADDRESS, UsdtAbi, provider)
        const raw = await usdt.balanceOf(this.walletAddress)
        const decimals = await usdt.decimals()
        const formatted = ethers.formatUnits(raw, decimals)
        const num = parseFloat(formatted)
        this.walletBalance = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      } catch (e) {
        console.error('fetchUsdtBalance failed', e)
      }
    },
    async fetchOcPrice() {
      if (!ROUTER_ADDRESS || !OC_ADDRESS || !ALK_ADDRESS || !USDT_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const router = new ethers.Contract(ROUTER_ADDRESS, RouterAbi, provider)
        const amounts = await router.getAmountsOut(ethers.parseUnits('1', 18), [OC_ADDRESS, ALK_ADDRESS, USDT_ADDRESS])
        const price = parseFloat(ethers.formatUnits(amounts[2], 18))
        this.ocPrice = price < 0.01 ? price.toFixed(6) : price < 1 ? price.toFixed(4) : price.toFixed(2)
      } catch (e) {
        console.error('fetchOcPrice failed', e)
      }
    },
    async fetchSellTax() {
      if (!OC_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const oc = new ethers.Contract(OC_ADDRESS, OpenClawAbi, provider)
        const [rate, base] = await Promise.all([oc.sellTaxRate(), oc.TAX_BASE()])
        this.sellTax = (Number(rate) / Number(base) * 100).toFixed(1)
      } catch (e) {
        console.error('fetchSellTax failed', e)
      }
    },
    async connectWallet() {
      if (!window.ethereum) {
        this.$toast(this.t.installWallet)
        return
      }
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
        wallet.address = accounts[0]
      } catch (e) {
        console.error('connectWallet failed', e)
      }
    },
    changeLang(lang) {
      this.lang = lang
      localStorage.setItem('lang', lang)
    },
    async fetchBonusPool() {
      if (!OC_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const oc = new ethers.Contract(OC_ADDRESS, OpenClawAbi, provider)
        const vault = await oc.rewardVault()
        const raw = await oc.balanceOf(vault)
        const balance = parseFloat(ethers.formatUnits(raw, 18))
        this.bonusPoolAmount = balance.toLocaleString('en-US', { maximumFractionDigits: 2 })
      } catch (e) {
        console.error('fetchBonusPool failed', e)
      }
    },
    async fetchTodayYield() {
      if (!OC_ADDRESS || !ALK_ADDRESS || !USDT_ADDRESS || !ROUTER_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const oc = new ethers.Contract(OC_ADDRESS, OpenClawAbi, provider)
        const alk = new ethers.Contract(ALK_ADDRESS, UsdtAbi, provider)
        const router = new ethers.Contract(ROUTER_ADDRESS, RouterAbi, provider)

        const pairAddress = await oc.pairAddress()
        const [ocReserve, ocAmounts] = await Promise.all([
          oc.balanceOf(pairAddress),
          router.getAmountsOut(ethers.parseUnits('1', 18), [OC_ADDRESS, ALK_ADDRESS, USDT_ADDRESS]),
        ])
        const ocPrice = parseFloat(ethers.formatUnits(ocAmounts[2], 18))
        const lpDepth = parseFloat(ethers.formatUnits(ocReserve, 18)) * ocPrice

        const tiers = [
          [30000000, '1.00'], [25000000, '0.95'], [20000000, '0.90'],
          [15000000, '0.80'], [10000000, '0.70'], [5000000, '0.60'],
          [2000000, '0.45'], [0, '0.35'],
        ]
        this.todayYield = tiers.find(t => lpDepth >= t[0])[1] + '%'
      } catch (e) {
        console.error('fetchTodayYield failed', e)
      }
    },
  }
}
</script>

<style lang="less" scoped>
.home-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  padding-bottom: 90px;
  position: relative;
}
.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
}

// ---- Wallet Card ----
.wallet-card {
  margin-top: 12px;
  position: relative;
}

.wallet-card-bg {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
}

.wallet-card-content {
  position: absolute;
  inset: 0;
  padding: 20px;
  z-index: 1;
}

.wallet-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.wallet-balance-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 11px 12px;
}

.eye-icon {
  width: 22px;
  height: 22px;
  cursor: pointer;
  object-fit: contain;
  flex-shrink: 0;
}

.wallet-amount {
  font-size: 24px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
  line-height: 1;
  margin-left: 30px;
}

.connect-btn {
  margin-top: 20px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.connect-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.connect-btn-text {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

// ---- Pool Health ----
.pool-health-card {
  margin-top: 16px;
  border-radius: 10px;
  padding: 15px 0 20px;
  position: relative;
  // display: flex;
  // flex-direction: column;
  // align-items: center;
  // background: linear-gradient(66deg, rgba(237, 35, 60, 0.66) 0%, rgba(237, 35, 60, 0) 79%), rgba(255, 255, 255, 0.05);
  background-image: url(../assets/home/progress-border.png);
  background-size: cover;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 10px;
    padding: 1px;
    // background: linear-gradient(99deg, #fff 2%, transparent 16%),
    //   linear-gradient(99deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.pool-health-bg {
  width: 174px;
  height: 174px;
  object-fit: contain;
}

.pool-health-score {
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  font-family: 'DIN Alternate', sans-serif;
  margin-top: -140px;
  position: relative;
  z-index: 1;
}

.pool-health-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  margin-top: 10px;
  letter-spacing: 0.5px;
}

.pool-health-badge {
  margin-top: 7px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 90px;
  padding: 3px 12px;
}

.badge-text {
  font-size: 12px;
  color: #14d990;
  font-weight: 500;
}

// ---- Info Row (side by side) ----
.info-row {
  margin: 0 10px;
  margin-top: 52px;
  display: flex;
  gap: 15px;
}

.info-card {
  flex: 1;
  border-radius: 8px;
  padding: 15px 14px 7px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(104deg, #fff 0%, transparent 0%),
      linear-gradient(105deg, rgba(243, 40, 90, 1) 0%, rgba(255, 255, 255, 0.1) 0%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.info-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.info-value {
  font-size: 22px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
}

.info-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

// ---- Full-width Cards ----
.full-card {
  margin-top: 16px;
  border-radius: 4px;
  padding: 22px 12px 15px;
  position: relative;
  // background: linear-gradient(112deg, rgba(237, 35, 60, 1) 0%, rgba(237, 35, 60, 0) 49%), rgba(255, 255, 255, 0.05);
  background-image: url(../assets/home/border.png);
  background-size: cover;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    // background: linear-gradient(118deg, #fff 2%, transparent 16%),
    //   linear-gradient(119deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.full-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.full-card-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.full-card-value {
  font-size: 22px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
}

.full-card-bottom {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.full-card-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 500;
}

.full-card-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 500;
}

// ---- Toast ----
.toast {
  position: fixed;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}
</style>
